import { useState, useEffect } from 'react';
import CateSubmenuItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/CateSubmenuItem';
import CategorySubmenuList from '../CategorySubmenuList';
import * as S from './style';

/** 헤더 높이. 헤더를 못 찾았을 때 쓰는 기본값 */
const HEADER_HEIGHT = 50;

/**
 * 레이어가 시작할 위치(헤더 하단)를 잰다.
 *
 * 평소에는 `.inner-header` 가 `position: fixed; top: 0` 이라 항상 50px 이지만,
 * 앱설치 배너(airbridge)가 열리면 `body.airbridge-banner-opened` 가 붙으면서
 * 헤더가 `sticky` 로 바뀌어 배너 높이만큼 내려간다. (SubLayout/style.ts 참고)
 * 배너는 문서 흐름에 있어서 스크롤하면 사라지므로 내려가는 양이 고정값이 아니다.
 * 그래서 상수를 더하지 않고 헤더의 실제 위치를 잰다.
 *
 * 레이어가 열려 있는 동안은 body 가 no-scroll 이라 이 값이 변하지 않는다.
 */
const getHeaderBottom = () => {
  const header = document.querySelector('#header .inner-header');
  if (!header) return HEADER_HEIGHT;

  return Math.max(header.getBoundingClientRect().bottom, 0);
};

interface CategoryItem {
  text: string;
  value: string;
  categoryList?: CategoryItem[];
}

interface CategoryDimLayerProps {
  isOpen: boolean;
  onClose: () => void;
  categoryList: CategoryItem[];
  activeCategoryValue?: string;
  activeCategoryValueDepth2?: string;
  onClickDepth2?: (
    depth1Value: string,
    depth2Value: string,
    depth2Text: string
  ) => void;
  onClickDepth1?: (depth1Value: string, hasChildren: boolean) => void;
}

const CategoryDimLayer = ({
  isOpen,
  onClose,
  categoryList,
  activeCategoryValue,
  activeCategoryValueDepth2,
  onClickDepth2,
  onClickDepth1,
}: CategoryDimLayerProps) => {
  const [activeDepth1, setActiveDepth1] = useState<CategoryItem | null>(null);
  /** 레이어 시작 위치(헤더 하단) */
  const [top, setTop] = useState(HEADER_HEIGHT);

  // 열릴 때마다 헤더 위치를 다시 잰다 (앱설치 배너 유무·스크롤 위치에 따라 달라진다)
  useEffect(() => {
    if (isOpen) setTop(getHeaderBottom());
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const active =
        categoryList.find(c => c.value === activeCategoryValue) ||
        categoryList[0];
      setActiveDepth1(active);
    }
  }, [isOpen, categoryList, activeCategoryValue]);

  if (!isOpen) return null;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <S.CategoryDimLayer
      className="category-dim-layer"
      $top={top}
      onClick={e => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <CategorySubmenuList>
        <ul className="list">
          {categoryList?.map(dpt1 => (
            <CateSubmenuItem
              key={dpt1.value}
              text={dpt1.text}
              active={activeDepth1?.value === dpt1.value}
              onClick={() => {
                setActiveDepth1(dpt1);
                onClickDepth1?.(dpt1.value, !!dpt1.categoryList?.length);
              }}
            />
          ))}
        </ul>
        <div className="area-sub">
          {activeDepth1?.categoryList &&
            activeDepth1.categoryList.length > 0 && (
              <ul className="sub-list">
                {activeDepth1.categoryList.map(dpt2 => (
                  <CateSubmenuItem
                    key={dpt2.value}
                    text={dpt2.text}
                    active={
                      activeCategoryValueDepth2 === dpt2.value &&
                      activeCategoryValue === activeDepth1.value
                    }
                    onClick={() => {
                      onClickDepth2?.(
                        activeDepth1.value,
                        dpt2.value,
                        dpt2.text
                      );
                      onClose();
                    }}
                  />
                ))}
              </ul>
            )}
        </div>
      </CategorySubmenuList>
    </S.CategoryDimLayer>
  );
};

export default CategoryDimLayer;
